/* eslint-disable no-param-reassign */
import {
  ActionReducerMapBuilder,
  createAsyncThunk,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit';

import { filterType } from '../../config/enum';
import { CommonFilterProperties, FilterUpdateProperties } from '../../interface/filter.interface';
import {
  APIResponseInterface,
  FilterRoleInterFace,
  RequestRoleInterFace,
  RoleInitialProperties,
  RoleInterFace,
  RoleListInterFace,
} from '../../interface/role-management.interface';
import {
  ClearFilters,
  ConfigureListFilterResponse,
  RemoveSingleFilterFromList,
  UpdateCommonFilter,
} from '../../shared-functions/CommonFilters';
import ConvertUTCtoDate from '../../shared-functions/ConvertUTCtoDate';
import { removeEmptyKeys } from '../../shared-functions/DataTime';
import roleApi from '../../utils/api/roles';

// a thunk to fetch users
export const fetchAllRoles = createAsyncThunk<APIResponseInterface, RequestRoleInterFace>(
  'role-management/fetchAllRoles',
  async (data: RequestRoleInterFace, { dispatch }) => {
    try {
      const response = await roleApi.fetchRoles(removeEmptyKeys(data));
      return {
        ...response.data,
        results: response.data.results.map((stationData: RoleInterFace) => ({
          ...stationData,
          date: ConvertUTCtoDate(stationData.createdAt),
          id: stationData._id,
        })),
      };
    } catch (error: any) {
      dispatch({
        type: 'notification/setNotificationDetails',
        payload: {
          type: 'error',
          notificationDetails: {
            statusCode: error?.response?.data?.code || 200,
            message: error?.response?.data?.message || 'Something went wrong!',
          },
        },
      });
      throw error;
    }
  },
);
// a thunk to fetch users
export const fetchAllRolesWithOutPage = createAsyncThunk<RoleInterFace[]>(
  'role-management/fetchAllRolesWithOutPage',
  async () => {
    const response = await roleApi.fetchAllRoles();
    return response.data.map((role: RoleListInterFace) => ({
      ...role,
      id: role._id,
    }));
  },
);

export const fetchListFilters = createAsyncThunk<CommonFilterProperties[], void>(
  'role-management/fetchListFilters',
  async (_, { dispatch }) => {
    try {
      const response = await roleApi.fetchListFilters();
      const updatedResponse: CommonFilterProperties[] = ConfigureListFilterResponse(response.data);
      return updatedResponse;
    } catch (error: any) {
      dispatch({
        type: 'notification/setNotificationDetails',
        payload: {
          type: 'error',
          notificationDetails: {
            statusCode: error?.response?.data?.code || 200,
            message: error?.response?.data?.message || 'Something went wrong!',
          },
        },
      });
      throw error;
    }
  },
);

export const fetchAllPermissions = createAsyncThunk<{ label: string; value: string }[]>(
  'role-management/fetchAllPermissions',
  async (_data, { dispatch }) => {
    try {
      const response = await roleApi.fetchAllPermissions();
      return response.data.map((permission: string) => ({
        label: permission,
        value: permission,
      }));
    } catch (error: any) {
      dispatch({
        type: 'notification/setNotificationDetails',
        payload: {
          type: 'error',
          notificationDetails: {
            statusCode: error?.response?.data?.code || 200,
            message: error?.response?.data?.message || 'Something went wrong!',
          },
        },
      });
      throw error;
    }
  },
);

const stationFilter: CommonFilterProperties[] = [
  {
    name: 'searchPlaceholder',
    key: 'searchBy',
    type: filterType.TEXT,
    value: '',
    applyFilter: false,
    enable: true,
    searchBy: true,
  },
];

// Initial state
const initialState: RoleInitialProperties = {
  roles: [],
  roleList: [],
  permissionsList: [],
  loading: false,
  page: 1,
  limit: 10,
  totalPages: 0,
  totalResults: 0,
  filter: {},
  filters: stationFilter,
  filterListLoading: false,
  isFilterUpdated: false,
  sortBy: '',
  sortField: '',
};

// Create the slice
export const roleSlice = createSlice({
  name: 'role-management',
  initialState,
  reducers: {
    /**
     * Set role filter data function
     * @param state
     * @param action
     */
    setRoleFilterData: (
      state: RoleInitialProperties,
      action: PayloadAction<FilterRoleInterFace>,
    ) => {
      state.filter = action.payload;
    },

    /**
     * Function that reset slice to initial state
     * @param state
     */
    resetRoleToInitialState: (state) => {
      Object.assign(state, initialState);
    },

    /**
     * Update role management filters
     * @param state
     * @param action
     */
    updateRoleFilters: (state, action: PayloadAction<FilterUpdateProperties>) => {
      const payload = { ...action.payload };
      if (payload.type === 'add') {
        const { updatedCommonFilter, isFilterUpdated } = UpdateCommonFilter(
          state.filters,
          action.payload,
        );
        state.filters = updatedCommonFilter;
        state.isFilterUpdated = isFilterUpdated;
        // state.filters = UpdateCommonFilter(state.filters, action.payload);
      } else if (payload.type === 'remove') {
        state.filters = RemoveSingleFilterFromList(state.filters, payload.key);
        state.isFilterUpdated = true;
      } else if (payload.type === 'clear') {
        state.filters = ClearFilters(state.filters);
        state.isFilterUpdated = true;
      }
    },
    /**
     * function for set sortBy
     * @param state
     * @param action
     */
    setSortData: (state, action: PayloadAction<{ sortField: string; sortBy: 'asc' | 'desc' }>) => {
      state.sortBy = action.payload.sortBy;
      state.sortField = action.payload.sortField;
    },
  },
  /**
   * Extra reducers functions
   * @param builder
   */
  extraReducers: (builder: ActionReducerMapBuilder<RoleInitialProperties>) => {
    builder
      .addCase(fetchAllRoles.pending, (state: RoleInitialProperties) => {
        state.loading = true;
      })
      .addCase(
        fetchAllRoles.fulfilled,
        (state: RoleInitialProperties, action: PayloadAction<APIResponseInterface>) => {
          state.loading = false;
          state.roles = action.payload.results;
          state.page = action.payload.page;
          state.limit = action.payload.limit;
          state.totalPages = action.payload.totalPages;
          state.totalResults = action.payload.totalResults;
        },
      )
      .addCase(fetchAllRoles.rejected, (state) => {
        state.loading = false;
      });

    builder
      .addCase(fetchAllRolesWithOutPage.pending, (state: RoleInitialProperties) => {
        state.loading = true;
      })
      .addCase(
        fetchAllRolesWithOutPage.fulfilled,
        (state: RoleInitialProperties, action: { payload: RoleListInterFace[] }) => {
          state.loading = false;
          state.roleList = action.payload;
        },
      )
      .addCase(fetchAllRolesWithOutPage.rejected, (state) => {
        state.loading = false;
      });

    builder
      .addCase(fetchAllPermissions.pending, (state: RoleInitialProperties) => {
        state.loading = true;
      })
      .addCase(
        fetchAllPermissions.fulfilled,
        (
          state: RoleInitialProperties,
          action: PayloadAction<{ label: string; value: string }[]>,
        ) => {
          state.loading = false;
          state.permissionsList = [...action.payload];
        },
      )
      .addCase(fetchAllPermissions.rejected, (state: RoleInitialProperties) => {
        state.loading = false;
        // Handle error if needed
      });
    builder
      .addCase(fetchListFilters.pending, (state) => {
        state.filterListLoading = true;
      })
      .addCase(
        fetchListFilters.fulfilled,
        (state, action: PayloadAction<CommonFilterProperties[]>) => {
          state.filterListLoading = false;
          state.filters = [...(state.filters || []), ...action.payload];
        },
      )
      .addCase(fetchListFilters.rejected, (state) => {
        state.filterListLoading = false;
      });
  },
});

// Export the actions
export const { setRoleFilterData, resetRoleToInitialState, updateRoleFilters, setSortData } = roleSlice.actions;

// Export the reducer
export default roleSlice.reducer;
